import { Button, Group, Modal, Stack, Stepper, Text, Title } from '@mantine/core'
import { modals, useModals } from '@mantine/modals'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { MODAL_OVER_MODAL_Z_INDEX } from '@/configs/componentDefaults'
import { useAllEsks } from '@/hooks/useAllEsks'
import { usePresentationStyles } from '@/hooks/usePresentationStyles'
import {
  addFiles,
  type BulkDefaults,
  type BulkUploadMode,
  type ConfirmedFile,
  type DefaultField,
  describeDefaultChange,
  followDefaults,
  formatFileSize,
  INITIAL_DEFAULTS,
  resolvedDeliveryMode,
  resolvedSidekick,
  type StagedFile,
  titleError,
  totalSize,
} from '@/utils/bulkContentUpload'
import { deliveryModeLabel } from '@/utils/deliveryModes'
import { sidekickSelectOptions } from '@/utils/sidekicks'

import {
  bulkUploadCopy,
  byFileTypeLabel,
  defaultAppliedText,
  discardTitle,
  resetAllDone,
  reviewSummary,
} from './bulkUploadCopy'
import { type DeliveryModeOption, type Highlight, ReviewStep } from './ReviewStep'
import { SelectFilesStep, type SkippedFiles } from './SelectFilesStep'

type BulkUploadWizardProps = {
  mode: BulkUploadMode
  opened: boolean
  /** The module being edited, in supplementary mode. */
  moduleTitle?: string
  onClose: () => void
  /** Resolving false keeps the wizard open with the selection intact, as when the job can't start. */
  onConfirm: (files: ConfirmedFile[]) => void | Promise<boolean>
}

const NO_SKIPPED: SkippedFiles = { unsupported: [], tooLarge: [] }
/** Long enough for the highlight's fade to finish. */
const HIGHLIGHT_MS = 1300

export function BulkUploadWizard({ mode, opened, moduleTitle, onClose, onConfirm }: BulkUploadWizardProps) {
  const copy = bulkUploadCopy[mode]
  const [step, setStep] = useState(0)
  const [files, setFiles] = useState<StagedFile[]>([])
  const [skipped, setSkipped] = useState<SkippedFiles>(NO_SKIPPED)
  const [defaults, setDefaults] = useState<BulkDefaults>(INITIAL_DEFAULTS)
  const [status, setStatus] = useState('')
  const [highlight, setHighlight] = useState<Highlight>()
  const [confirming, setConfirming] = useState(false)
  // Escape on the discard confirmation would otherwise reach this modal too and reopen it.
  const { modals: confirmations } = useModals()

  const { allEsks } = useAllEsks()
  const stylesQuery = usePresentationStyles()

  const sidekickOptions = useMemo(() => sidekickSelectOptions(allEsks), [allEsks])
  const sidekickLabels = useMemo(
    () => new Map(sidekickOptions.flatMap(group => group.items).map(item => [item.value, item.label])),
    [sidekickOptions],
  )
  const deliveryModes = useMemo<DeliveryModeOption[]>(
    () =>
      (stylesQuery.data ?? []).map(style => ({
        value: style.name,
        label: deliveryModeLabel(style.name, style.displayName || style.name),
      })),
    [stylesQuery.data],
  )

  // Cleared once the fade is done, or a row scrolled back into view would fade again.
  useEffect(() => {
    if (!highlight) return
    const timer = setTimeout(() => setHighlight(undefined), HIGHLIGHT_MS)
    return () => clearTimeout(timer)
  }, [highlight])

  const close = () => {
    setStep(0)
    setFiles([])
    setSkipped(NO_SKIPPED)
    setDefaults(INITIAL_DEFAULTS)
    setStatus('')
    setHighlight(undefined)
    onClose()
  }

  const requestClose = () => {
    if (files.length === 0) {
      close()
      return
    }
    modals.openConfirmModal({
      title: discardTitle,
      centered: true,
      zIndex: MODAL_OVER_MODAL_Z_INDEX,
      children: <Text size="sm">{copy.discardBody}</Text>,
      labels: { confirm: 'Discard Files', cancel: 'Keep Editing' },
      confirmProps: { color: 'red' },
      onConfirm: close,
    })
  }

  // The status line describes the last default change, so any other edit to the files retires it.
  const editFiles = useCallback((update: (current: StagedFile[]) => StagedFile[]) => {
    setFiles(update)
    setStatus('')
  }, [])

  const add = (incoming: File[]) => {
    const result = addFiles(files, incoming)
    editFiles(() => result.files)
    setSkipped({ unsupported: result.unsupported, tooLarge: result.tooLarge })
  }

  const remove = useCallback(
    (name: string) => editFiles(current => current.filter(f => f.file.name !== name)),
    [editFiles],
  )

  const change = useCallback(
    (name: string, patch: Partial<StagedFile>) =>
      editFiles(current => current.map(f => (f.file.name === name ? { ...f, ...patch } : f))),
    [editFiles],
  )

  const styleNames = useMemo(() => deliveryModes.map(m => m.value), [deliveryModes])

  const modeLabels = useMemo(() => new Map(deliveryModes.map(m => [m.value, m.label])), [deliveryModes])

  const changeDefault = (field: DefaultField, value: string | null) => {
    const next: BulkDefaults =
      field === 'eskId' ? { ...defaults, eskId: value! } : { ...defaults, deliveryModeName: value }
    const change = describeDefaultChange(files, styleNames, field, defaults, next)
    const label =
      field === 'eskId'
        ? (sidekickLabels.get(next.eskId) ?? next.eskId)
        : ((next.deliveryModeName && modeLabels.get(next.deliveryModeName)) ?? byFileTypeLabel)
    setDefaults(next)
    setStatus(defaultAppliedText(label, change))
    setHighlight(current => ({ field: change.field, names: new Set(change.changed), token: (current?.token ?? 0) + 1 }))
  }

  const resetAll = () => {
    setFiles(followDefaults)
    setStatus(resetAllDone)
  }

  const confirm = async () => {
    setConfirming(true)
    try {
      const started = await onConfirm(
        files.map(f => ({
          ...f,
          title: f.title.trim(),
          eskId: resolvedSidekick(f, defaults),
          deliveryModeName: mode === 'modules' ? (resolvedDeliveryMode(f, styleNames, defaults) ?? null) : null,
        })),
      )
      if (started !== false) close()
    } finally {
      setConfirming(false)
    }
  }

  const canConfirm =
    files.length > 0 &&
    files.every(f => !titleError(f.title) && (mode !== 'modules' || resolvedDeliveryMode(f, styleNames, defaults)))

  return (
    <Modal
      opened={opened}
      onClose={requestClose}
      closeOnClickOutside={false}
      closeOnEscape={confirmations.length === 0}
      size="64rem"
      centered
      title={
        <Stack gap={4}>
          <Title order={3}>{copy.title}</Title>
          {step === 0 && (
            <Text size="sm" c="dimmed">
              {copy.subtitle(moduleTitle)}
            </Text>
          )}
        </Stack>
      }>
      <Stack gap="lg">
        <Stepper active={step} size="sm">
          {copy.steps.map(label => (
            <Stepper.Step key={label} label={label} />
          ))}
        </Stepper>

        {step === 0 ? (
          <SelectFilesStep
            mode={mode}
            files={files}
            skipped={skipped}
            onAdd={add}
            onRemove={remove}
            onRemoveAll={() => {
              editFiles(() => [])
              setSkipped(NO_SKIPPED)
            }}
          />
        ) : (
          <ReviewStep
            mode={mode}
            files={files}
            defaults={defaults}
            sidekickOptions={sidekickOptions}
            sidekickLabels={sidekickLabels}
            deliveryModes={deliveryModes}
            modeLabels={modeLabels}
            deliveryModesFailed={stylesQuery.isError}
            status={status}
            highlight={highlight}
            onChange={change}
            onRemove={remove}
            onDefaultChange={changeDefault}
            onResetAll={resetAll}
          />
        )}

        <Group justify="space-between">
          {step === 0 ? (
            <Button variant="default" onClick={requestClose}>
              Cancel
            </Button>
          ) : (
            <Button variant="default" onClick={() => setStep(0)}>
              Back
            </Button>
          )}

          {step === 0 ? (
            <Button disabled={files.length === 0} onClick={() => setStep(1)}>
              Continue to Review
            </Button>
          ) : (
            <Group gap="md">
              <Text size="sm" c="dimmed">
                {reviewSummary(files.length, formatFileSize(totalSize(files)))}
              </Text>
              <Button disabled={!canConfirm} loading={confirming} onClick={() => void confirm()}>
                {copy.confirmLabel(files.length)}
              </Button>
            </Group>
          )}
        </Group>
      </Stack>
    </Modal>
  )
}
